import React, { useState, useEffect, useMemo } from "react";
import { IonModal, IonIcon } from "@ionic/react";
import { chevronBackOutline, chevronForwardOutline, closeOutline } from "ionicons/icons";

interface DayDatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (dateISO: string) => void;
  /** YYYY-MM-DD – calendar opens showing this date's month */
  initialDate?: string;
  /** YYYY-MM-DD – earliest selectable date */
  minDate?: string;
  /** YYYY-MM-DD – latest selectable date */
  maxDate?: string;
}

const WEEK_DAYS = ["M", "T", "W", "T", "F", "S", "S"];

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const DayDatePicker: React.FC<DayDatePickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialDate,
  minDate,
  maxDate,
}) => {
  const [viewMonth, setViewMonth] = useState(new Date());
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialDate) {
        const [y, m] = initialDate.split("-").map(Number);
        setViewMonth(new Date(y, m - 1, 1));
        setSelected(initialDate);
      } else {
        setViewMonth(new Date());
        setSelected(null);
      }
    }
  }, [isOpen, initialDate]);

  const todayISO = useMemo(() => toISO(new Date()), []);

  const calendarDays = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    // Start from Monday (getDay() = 1 for Monday)
    const dayOfWeek = firstOfMonth.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - mondayOffset);

    const days: Date[] = [];
    const cursor = new Date(start);
    for (let i = 0; i < 42; i++) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [viewMonth]);

  const prevMonth = () =>
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));

  const nextMonth = () =>
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));

  const handleDayClick = (date: Date) => {
    const iso = toISO(date);
    if (minDate && iso < minDate) return;
    if (maxDate && iso > maxDate) return;
    setSelected(iso);
  };

  const handleGo = () => {
    if (selected) {
      onSelect(selected);
    }
  };

  const formatDisplay = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="day-date-picker-modal"
      style={
        {
          "--width": "400px",
          "--max-width": "95vw",
          "--height": "auto",
          // "--max-height": "85vh",
          "--border-radius": "24px",
          "--backdrop-opacity": "0.5",
          "--box-shadow": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        } as React.CSSProperties
      }
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          width: "100%",
          // maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
        }}
      >
        {/* Header with Gradient */}
        <div
          style={{
            background: "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)",
            padding: "28px 28px 40px",
            position: "relative",
          }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.35)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <IonIcon
              icon={closeOutline}
              style={{ color: "white", fontSize: "20px" }}
            />
          </button>

          {/* Title */}
          <div style={{ textAlign: "center" }}>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "white",
                margin: "0 0 8px",
                letterSpacing: "-0.5px",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              Go to Date
            </h2>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "14px",
                margin: 0,
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {selected ? formatDisplay(selected) : "Select a date to jump to"}
            </p>
          </div>
        </div>

        {/* Content Area with curved top */}
        <div
          style={{
            background: "#ffffff",
            marginTop: "-20px",
            borderRadius: "24px 24px 0 0",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Month Navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 24px 12px",
            }}
          >
            <button
              onClick={prevMonth}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f9fc";
                e.currentTarget.style.borderColor = "#05acd9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <IonIcon
                icon={chevronBackOutline}
                style={{ color: "#05acd9", fontSize: "18px" }}
              />
            </button>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {viewMonth.toLocaleString("default", { month: "long" })}{" "}
              {viewMonth.getFullYear()}
            </span>
            <button
              onClick={nextMonth}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f9fc";
                e.currentTarget.style.borderColor = "#05acd9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <IonIcon
                icon={chevronForwardOutline}
                style={{ color: "#05acd9", fontSize: "18px" }}
              />
            </button>
          </div>

          {/* Weekday Headers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              padding: "0 20px 4px",
            }}
          >
            {WEEK_DAYS.map((d, i) => (
              <div
                key={i}
                style={{
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#94a3b8",
                  fontFamily: "Figtree, sans-serif",
                }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              padding: "0 20px 16px",
            }}
          >
            {calendarDays.map((date, i) => {
              const iso = toISO(date);
              const inMonth = date.getMonth() === viewMonth.getMonth();
              const isToday = iso === todayISO;
              const isSelected = iso === selected;
              const disabled =
                !inMonth ||
                (!!minDate && iso < minDate) ||
                (!!maxDate && iso > maxDate);

              return (
                <button
                  key={i}
                  onClick={() => !disabled && handleDayClick(date)}
                  disabled={disabled}
                  style={{
                    height: "42px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: isSelected ? 700 : isToday ? 700 : 400,
                    borderRadius: "50%",
                    border: "none",
                    cursor: disabled ? "default" : "pointer",
                    transition: "all 0.15s ease",
                    fontFamily: "Figtree, sans-serif",
                    color: disabled
                      ? "#d1d5db"
                      : isSelected
                      ? "#ffffff"
                      : isToday
                      ? "#05acd9"
                      : "#374151",
                    background: isSelected
                      ? "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)"
                      : "transparent",
                    boxShadow: isSelected
                      ? "0 4px 12px rgba(5, 172, 217, 0.3)"
                      : "none",
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Go Button – Sticky Footer */}
          <div
            style={{
              padding: "16px 24px 24px",
              borderTop: "1px solid #f1f5f9",
              background:
                "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <button
              onClick={handleGo}
              disabled={!selected}
              style={{
                width: "100%",
                height: "48px",
                borderRadius: "30px",
                border: "none",
                fontSize: "17px",
                fontWeight: 600,
                color: "white",
                cursor: selected ? "pointer" : "default",
                fontFamily: "Figtree, sans-serif",
                letterSpacing: "-0.3px",
                transition: "all 0.2s ease",
                background: selected
                  ? "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)"
                  : "#cbd5e1",
                boxShadow: selected
                  ? "0 4px 16px rgba(5, 172, 217, 0.3)"
                  : "none",
                opacity: selected ? 1 : 0.6,
              }}
            >
              Go
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .day-date-picker-modal {
          --ion-backdrop-opacity: 0.5;
        }
      `}</style>
    </IonModal>
  );
};

export default DayDatePicker;
